import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { FilterBar } from "@/components/work-items/filter-bar";
import { NewWorkItemButtons } from "@/components/work-items/new-buttons";
import { QuickFilters } from "@/components/work-items/quick-filters";
import {
  WORK_ITEM_SORT_KEYS,
  WorkItemsTable,
  type WorkItemRow as WorkItemsTableRow,
} from "@/components/work-items/work-items-table";
import { api } from "@/lib/api";
import { usePersistedFilters } from "@/lib/persisted-filters";
import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import { useLoaderData, useLocation, useMatches, useSearch } from "@tanstack/react-router";
import { z } from "zod";

/**
 * `/projects/:slug/work-items` — the project's work-item list,
 * rendered inside the project layout. Port of the Next
 * `WorkItemsList` locked to one project (`forcedProjectId`): the
 * project column is hidden and the project filter dropdown is
 * dropped, but every other filter, the per-row workflow actions and
 * the bulk-edit toolbar are present — same component as the global
 * `/work-items` page.
 *
 * Shared by this route and the GitLab-native
 * `/{group}/{project}/-/work_items` route.
 */

const TYPE_OPTIONS = [
  { id: "feature", name: "Feature" },
  { id: "bug", name: "Bug" },
  { id: "standalone", name: "Task" },
  { id: "hotfix", name: "Hotfix" },
] as const;

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
] as const;

export const workItemsSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  priority: z.enum(["low", "normal", "high", "urgent"]).optional(),
  type: z.enum(["feature", "bug", "standalone", "hotfix"]).optional(),
  assignee: z.string().optional(),
  author: z.string().optional(),
  watcher: z.string().optional(),
  attachments: z.enum(["any"]).optional(),
  scope: z.enum(["open", "all"]).optional(),
  release: z.string().optional(),
  environment: z.enum(["dev", "test", "main", "none"]).optional(),
  dueAfter: z.string().optional(),
  dueBefore: z.string().optional(),
  ...sortSearchFields(WORK_ITEM_SORT_KEYS),
  ...paginationSearchFields,
});

type WorkItemsSearch = z.infer<typeof workItemsSearchSchema>;

/** Shape of `GET /api/work-items/list` — shared by every WI list route. */
export type WorkItemsListResponse = {
  workItems: WorkItemsTableRow[];
  total: number;
  page: number;
  pageSize: number;
  statuses: Array<{ id: string; name: string; color: string }>;
  users: Array<{ id: string; name: string | null }>;
  projects: Array<{ id: string; name: string }>;
  /** Releases of the scoped project (open, locked and closed) —
   *  non-empty only when a single project is scoped. */
  releases: Array<{ id: string; name: string; status: string }>;
};

/**
 * Fetch the project-scoped work-item list. Shared by the project
 * tab and the GitLab-native `/{group}/{project}/-/work_items` route.
 * The project is forced via `?project=<slug>`; every other filter
 * comes from the URL search.
 */
export function loadProjectWorkItems(
  projectSlug: string,
  deps: WorkItemsSearch,
): Promise<WorkItemsListResponse> {
  const qs = new URLSearchParams({ project: projectSlug });
  for (const [k, v] of Object.entries(deps)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  return api.get<WorkItemsListResponse>(`/api/work-items/list?${qs}`);
}
const PROJECT_WI_FILTER_FIELDS = [
  "q",
  "status",
  "priority",
  "type",
  "assignee",
  "author",
  "watcher",
  "attachments",
  "scope",
  "release",
  "environment",
  "dueAfter",
  "dueBefore",
] as const;

export function ProjectWorkItemsPage() {
  const { workItems, total, page, pageSize, statuses, users, releases } = useLoaderData({
    strict: false,
  }) as WorkItemsListResponse;
  const search = useSearch({ strict: false }) as WorkItemsSearch;
  const basePath = useLocation().pathname;
  const layout = useLoaderData({ from: "/_app" });
  const currentUserId = layout.user.id;
  // Filters survive across visits per project — the pathname
  // identity (`/projects/<slug>/work-items`) is part of the
  // storage key, so each project remembers its own set without
  // bleeding into the global `/work-items` list.
  usePersistedFilters(PROJECT_WI_FILTER_FIELDS);
  // "Mine" is the default for regular team members (devs / etc.)
  // since they're usually looking at their own queue. Admins +
  // accountants need the firehose. Customer accounts have no
  // assigned WIs by design — default them to "open" instead so
  // the listing actually shows something.
  const defaultMine =
    !layout.isCustomer &&
    layout.user.globalRole !== "admin" &&
    layout.user.globalRole !== "accountant";
  // Project context for the New-WI dialog — the page is already
  // locked to one project, so pre-fill (and lock) the picker. Read it
  // from whichever ancestor project layout is active (route-agnostic)
  // so this page works under BOTH the internal `/projects/$slug` route
  // and the GitLab-native `/$group/$project/-/work_items` route, which
  // share this component but not the same parent route id.
  const project = useMatches()
    .map((m) => (m.loaderData as { project?: { id: string } } | undefined)?.project)
    .find((p) => p != null);
  if (!project) throw new Error("ProjectWorkItemsPage rendered outside a project layout");

  // The exports run the same filter as the listing — hand the endpoint the
  // current search, minus the page window.
  const exportQs = new URLSearchParams({ project: project.id });
  for (const field of PROJECT_WI_FILTER_FIELDS) {
    const v = search[field];
    if (v !== undefined && v !== "") exportQs.set(field, String(v));
  }
  if (search.sort) exportQs.set("sort", search.sort);
  if (search.dir) exportQs.set("dir", search.dir);

  return (
    <div className="space-y-6 mt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{total} work items</p>
        <div className="flex items-center gap-2">
          <a
            href={`/api/work-items/export?${exportQs}`}
            download
            className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
          >
            Export CSV
          </a>
          <a
            href={`/api/work-items/export/pdf?${exportQs}`}
            download
            className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
          >
            Export PDF
          </a>
          <NewWorkItemButtons projectId={project.id} />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <QuickFilters currentUserId={currentUserId} defaultMine={defaultMine} />
        <SearchInput placeholder="Search work items..." />
        <FilterBar
          statuses={statuses.map((s) => ({ id: s.id, name: s.name }))}
          types={TYPE_OPTIONS.map((t) => ({ id: t.id, name: t.name }))}
          priorities={PRIORITY_OPTIONS.map((p) => ({ value: p.value, label: p.label }))}
          users={users.map((u) => ({ id: u.id, name: u.name ?? "(unnamed)" }))}
          /* Project picker hidden — the list is already locked to one. */
          projects={[]}
          releases={releases}
        />
      </div>

      <WorkItemsTable
        workItems={workItems}
        users={users.map((u) => ({ id: u.id, name: u.name ?? "(unnamed)" }))}
        releases={releases}
        hideProjectColumn
        urlSort={{ sort: search.sort, dir: search.dir }}
        currentUserId={currentUserId}
        isCustomer={layout.isCustomer}
        canSeeAll={layout.user.globalRole === "admin" || layout.user.globalRole === "accountant"}
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="project-work-items"
        basePath={basePath}
        searchParams={{
          q: search.q,
          status: search.status,
          priority: search.priority,
          type: search.type,
          assignee: search.assignee,
          author: search.author,
          watcher: search.watcher,
          attachments: search.attachments,
          scope: search.scope,
          release: search.release,
          environment: search.environment,
          dueAfter: search.dueAfter,
          dueBefore: search.dueBefore,
          sort: search.sort,
          dir: search.dir,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
